import { Button, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef } from "react";
import styles from "./CrmPackageMappingSkipWarningModal.module.css";

export type CrmPackageMappingSkipWarningModalProps = {
  activeSubscriptionCount: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
};

export function CrmPackageMappingSkipWarningModal({
  activeSubscriptionCount,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
}: CrmPackageMappingSkipWarningModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-warning-title"
        aria-describedby="skip-warning-message"
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.warningIcon} aria-hidden="true">
              <Icon icon="exclamation" size="large" />
            </div>
            <h2 id="skip-warning-title" className={styles.modalTitle}>
              Active Subscriptions Found
            </h2>
          </div>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p id="skip-warning-message" className={styles.warningMessage}>
            This mapping has <strong>{activeSubscriptionCount}</strong> active{" "}
            {activeSubscriptionCount === 1 ? "subscription" : "subscriptions"}. They won't be
            affected, but future webhooks for this product will be dropped.
          </p>
          <p className={styles.confirmMessage}>Do you want to continue?</p>
        </div>

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirm}
            disabled={isSubmitting}
            className={styles.warningButton}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner} />
                Saving...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
